import React, { useEffect, useState } from 'react'
import { Editor as ReactDraftEditor } from 'react-draft-wysiwyg'
import {
  EditorState,
  ContentState,
  convertFromHTML,
  DraftEntityInstance
} from 'draft-js'
import { stateToHTML } from 'draft-js-export-html'

import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'
import styled from '@emotion/styled'
import { useShop } from '../../contexts/Shop'

interface EditorProps {
  keyName: string
}

export const Editor = ({ keyName }: EditorProps) => {
  const { merchantOnChangeWithEditor, merchantGet } = useShop()
  const htmlContent = merchantGet(keyName)

  const [editorState, setEditorState] = useState(EditorState.createEmpty())

  // Set Editor's initial value if we have a saved data
  useEffect(() => {
    // Convert HTML to ContentState
    const blocksFromHTML = convertFromHTML(htmlContent)
    const contentState = ContentState.createFromBlockArray(
      blocksFromHTML.contentBlocks,
      blocksFromHTML.entityMap
    )

    // Set the content in the editor
    setEditorState(EditorState.createWithContent(contentState))
  }, [])

  const onEditorStateChange = (updatedEditor: EditorState) => {
    setEditorState(updatedEditor)
  }

  const convertContentToHTML = () => {
    const contentState = editorState.getCurrentContent()
    const html = stateToHTML(contentState)

    // prevents mutation if there are no changes
    if (htmlContent === html) return

    const options = {
      entityStyleFn: (entity: DraftEntityInstance) => {
        const entityType = entity.get('type').toLowerCase()
        if (entityType === 'link') {
          const data = entity.getData()
          return {
            element: 'a',
            attributes: {
              href: data.url,
              target: '_blank',
              rel: 'noopener noreferrer'
            }
          }
        }
      }
    }
    const currentContentAsHTML = stateToHTML(
      editorState.getCurrentContent(),
      options
    )

    merchantOnChangeWithEditor(keyName, currentContentAsHTML)
  }

  return (
    <>
      <EditorContainer onBlur={convertContentToHTML}>
        <ReactDraftEditor
          placeholder='Type your message here...'
          editorState={editorState}
          onEditorStateChange={onEditorStateChange}
          toolbarClassName='editor-toolbar'
          wrapperClassName='editor-wrapper'
          editorClassName='editor'
          toolbar={{
            options: ['inline', 'link', 'list', 'blockType'],
            link: {
              inDropdown: false,
              options: ['link', 'unlink']
            },
            inline: {
              options: ['bold', 'italic', 'underline']
            },
            list: {
              options: ['unordered', 'ordered'],
              popupClassName: 'editor-popup',
              dropdownClassName: 'editor-dropdown'
            },
            blockType: {
              inDropdown: false,
              options: ['H1']
            }
          }}
        />
      </EditorContainer>
    </>
  )
}

const EditorContainer = styled.div`
  .editor-wrapper {
    width: 100%;
    max-width: 320px;
  }

  .editor-toolbar,
  .editor {
    border-radius: 4px;
    border: 1px solid #d1d5db;

    label.rdw-link-modal-target-option {
      display: none;
    }

    .rdw-link-modal {
      left: -100%;
    }
  }

  .editor {
    padding: 8px 16px;
    cursor: text;
  }
`
